import type { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import axios from 'axios'
import { StorageService } from '@/services/storage.service.ts'

export class ApiService {
  private readonly timeout: number
  private service: AxiosInstance
  private readonly authFn: Function | null
  private readonly tokenStorageKey: string

  constructor(authFn: Function, headers: object, tokenStorageKey: string, baseUrl: string, timeout: number) {
    this.authFn = authFn
    this.timeout = timeout
    this.tokenStorageKey = tokenStorageKey
    this.service = axios.create({
      timeout,
      headers
    })
    this.service.defaults.baseURL = baseUrl
    this.service.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const bearerToken = StorageService.getDataFromStorage('sessionStorage', tokenStorageKey)
        if (bearerToken) config.headers.Authorization = `Bearer ${bearerToken}`
        return config
      },
      (error: AxiosError) => Promise.reject(error)
    )
    this.service.interceptors.response.use((response: AxiosResponse) => response, this.onRejected)
  }

  onRejected(error: AxiosError): void {
    const { message, name, response } = error
    throw { message, name, status: response?.status }
  }

  setToken(authToken: string) {
    StorageService.addDataToStorage('sessionStorage', this.tokenStorageKey, authToken)
  }

  get(route: string, config: object = {}) {
    const url = this.createUrl(route)
    return this.executeRequest(() => this.service.get(url, config))
  }

  post(route: string, data: object, config: object = {}) {
    const url = this.createUrl(route)
    return this.executeRequest(() => this.service.post(url, data, config))
  }

  put(route: string, data: object, config: object = {}) {
    const url = this.createUrl(route)
    return this.executeRequest(() => this.service.put(url, data, config))
  }

  deleteCall(route: string, config: object = {}) {
    const url = this.createUrl(route)
    return this.executeRequest(() => this.service.delete(url, config))
  }

  query(route: string, params: object) {
    const url = this.createUrl(route)
    return this.executeRequest(() => this.service.get(url, params))
  }

  async executeRequest(request: any, retries = 3) {
    try {
      return await request.call()
    } catch (error) {
      return await this.handleError(request, error, retries)
    }
  }

  async handleError(request: any, error: any, retries: number) {
    const { status, message } = error
    if (status === 0) {
      throw 'Network error'
    } else if (status === 401 && this.authFn) {
      try {
        await this.authFn // @TODO: this.authFn is referenced but never called; add brackets '()' so the client reconnects to the api service properly
        return await request.call()
      } catch (error) {
        console.error('Authentication error')
        throw error
      }
    } else if (status === 408 || status === 504 || message === `timeout of ${this.timeout}ms exceeded`) {
      for (let i = 1; i <= retries; i++) {
        try {
          return await this.executeDelayed(request, i * 1000)
        } catch (error) {
          console.error('Request retry error')
          if (i >= retries) throw error
        }
      }
    } else {
      throw error
    }
  }

  executeDelayed(fn: any, delay = 1000) {
    return new Promise((resolve, reject) => {
      setTimeout(async () => {
        try {
          const result = await fn.call()
          resolve(result)
        } catch (error) {
          reject(error)
        }
      }, delay)
    })
  }

  createUrl(route: string): string {
    return `${this.service.defaults.baseURL}/${route}`
  }
}
